import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import EmojiPicker, {
  DEFAULT_EMOJI_FOR_CUSTOM_BUDGET_CATEGORY,
} from '../../../../../../../shared/EmojiPicker';
import {
  emojiToUnicode,
  unicodeToEmoji,
} from '../../../../../../../shared/emojiUnicode';
import {
  BudgetItemComputed,
  CreateCustomBudgetItemRequest,
  Entity,
  PatchCustomBudgetItemRequest,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  formatCurrency,
  formatNumberWithCommas,
} from '../../../../../../../utils/CurrencyUtils';
import { InputNumber } from 'antd';
import {
  formatCurrencyInput,
  parseCurrencyInput,
} from '../../../../../../../utils/currencyFormatHelpers';

export const MIN_MONTHS = 1;
export const MAX_MONTHS = 12;

// Shown when the API rejects the save for a reason it can't state in words the
// customer would recognize — a schema rejection naming `emojiUnicode`, say.
// renderToast appends "Please email support@tachusa.com if this issue persists.
// [400]", so these name the three things the customer can change and stop
// there.
export const SAVE_FAILED_MESSAGE =
  "We couldn't save this savings category. Check the name, amount, and emoji, then try again.";
export const UPDATE_FAILED_MESSAGE =
  "We couldn't update this savings category. Check the name, amount, and emoji, then try again.";

export function clampMonthInterval(n: any): number {
  const v = Number(n) || 0;
  if (v < MIN_MONTHS) return MIN_MONTHS;
  if (v > MAX_MONTHS) return MAX_MONTHS;
  return v;
}

export function blockNonIntegerKeys(e: React.KeyboardEvent<HTMLInputElement>) {
  // Disallow minus, plus, exponent, dot
  if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
}

type SavingsCategoryFormData = {
  emoji: string;
  categoryName: string;
  targetAmount: number;
  monthInterval: number;
};

export const AddSavingsCategoryModal = ({
  isOpen,
  onClose,
  editBudgetItem,
}: {
  isOpen: boolean;
  onClose: () => void;
  editBudgetItem?: BudgetItemComputed;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    control,
    setValue,
  } = useForm<SavingsCategoryFormData>();
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(false);
  const { userRecord, userId } = useUser();
  const queryClient = useQueryClient();
  const wAmount = watch('targetAmount');
  const wMonths = watch('monthInterval');
  const wName = watch('categoryName');

  const isFiniteNumber = (v: unknown): v is number =>
    typeof v === 'number' && Number.isFinite(v);

  const explanation = useMemo(() => {
    const amountOk = isFiniteNumber(wAmount) && wAmount > 0;
    const monthsOk = isFiniteNumber(wMonths) && wMonths >= 1 && wMonths <= 12;
    const nameOk = !!wName?.trim();

    if (!amountOk || !monthsOk || !nameOk) return '';

    const categoryLower = wName.toLowerCase();
    const perMonth = wMonths > 1 ? wAmount / wMonths : undefined;

    return `This means you will save $${formatNumberWithCommas(wAmount)} every ${
      wMonths === 1 ? 'month' : `${wMonths} months`
    } for ${categoryLower}.${wMonths > 1 ? ` This is ${formatCurrency(perMonth ?? 0)} per month.` : ''}`;
  }, [wAmount, wMonths, wName]);

  // Initialize form with edit data if provided
  useEffect(() => {
    if (editBudgetItem && isOpen) {
      // Fall back to the same emoji the picker *displays* for a blank value.
      // Storing '' here while the picker shows the default made the save send
      // an empty emojiUnicode, which the API rejects.
      setValue(
        'emoji',
        unicodeToEmoji(editBudgetItem.emoji_unicode) ||
          DEFAULT_EMOJI_FOR_CUSTOM_BUDGET_CATEGORY
      );
      setValue('categoryName', editBudgetItem.category_display_name);
      setValue(
        'targetAmount',
        (editBudgetItem.customer_selected_amount ?? 0) /
          (12 / editBudgetItem.time_window_in_months)
      );
      setValue('monthInterval', editBudgetItem.time_window_in_months || 1);
    }
  }, [editBudgetItem, isOpen, setValue]);

  const onSubmit = (data: SavingsCategoryFormData) => {
    if (editBudgetItem) {
      updateCustomBudgetItem(
        data,
        userRecord?.userType === Entity.Customer ? userRecord?.customer_id : '',
        editBudgetItem.category
      );
    } else {
      createCustomBudgetItem(
        data,
        userRecord?.userType === Entity.Customer ? userRecord?.customer_id : ''
      );
    }
  };

  const createCustomBudgetItem = async (
    savingsInfo: SavingsCategoryFormData,
    customer_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi({
        url: '/budgetItems/' + customer_id,
        requestType: 'post',
        body: {
          annualBudgetedAmount: Number(
            Number(savingsInfo?.targetAmount) *
              (12 / savingsInfo?.monthInterval)
          ),
          timeWindowInMonths: Number(savingsInfo?.monthInterval),
          categoryDisplayName: savingsInfo?.categoryName,
          emojiUnicode: emojiToUnicode(savingsInfo?.emoji),
        } as CreateCustomBudgetItemRequest,
        errorMsg: SAVE_FAILED_MESSAGE,
        // A save can fail for a reason only the API knows — the category limit
        // being the one worth repeating verbatim. Raw schema-validation output
        // is filtered out by the wrapper, falling back to the copy above.
        preferServerErrorMessage: true,
      });
      const data = response?.data?.data?.customBudgetItem;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        response?.status === HttpStatusCode.Created
      ) {
        reset();
        onClose();
        toast.success('Custom budget category created successfully!');
        queryClient.invalidateQueries({ queryKey: ['budgetItems', userId] });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your custom budget category: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCustomBudgetItem = async (
    savingsInfo: SavingsCategoryFormData,
    customer_id: string,
    budget_item_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi({
        url: `/budgetItems/${customer_id}/${budget_item_id}`,
        requestType: 'patch',
        body: {
          annualBudgetedAmount: Number(
            savingsInfo?.targetAmount * (12 / savingsInfo?.monthInterval)
          ),
          timeWindowInMonths: Number(savingsInfo?.monthInterval),
          categoryDisplayName: savingsInfo?.categoryName,
          emojiUnicode: emojiToUnicode(savingsInfo?.emoji),
        } as PatchCustomBudgetItemRequest,
        errorMsg: UPDATE_FAILED_MESSAGE,
        // Same as the create call: surface the API's own reason when it is one
        // a customer can act on.
        preferServerErrorMessage: true,
      });
      if (response?.status === HttpStatusCode.Ok) {
        reset();
        onClose();
        toast.success('Custom budget category updated successfully!');
        queryClient.invalidateQueries({ queryKey: ['budgetItems', userId] });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating your custom budget category: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-[700px] max-h-[700px] max-w-[520px] rounded-[4px] bg-white sm:w-[95%] md:w-full">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  {editBudgetItem
                    ? 'Edit Savings Category'
                    : 'Save for New Expense'}
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                  weight="regular"
                  size={24}
                />
              </div>
              <form
                className="flex h-full max-h-[564px] flex-col gap-6 overflow-y-auto border-[1px] border-bordergray p-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Controller
                  name="emoji"
                  control={control}
                  defaultValue={DEFAULT_EMOJI_FOR_CUSTOM_BUDGET_CATEGORY}
                  render={({ field: { onChange, value } }) => (
                    <EmojiPicker value={value} onChange={onChange} />
                  )}
                />
                <div>
                  <label className="text-md block text-gray-700">
                    Custom Category Name
                  </label>
                  <input
                    type="text"
                    {...register('categoryName', {
                      required: 'Category name is required',
                    })}
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                  {errors.categoryName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.categoryName.message}
                    </p>
                  )}
                </div>

                {/* Target Amount Field */}
                <div>
                  <label className="text-md block text-gray-700">
                    Target Amount (USD)
                  </label>
                  <Controller
                    name="targetAmount"
                    control={control}
                    rules={{
                      required: 'Target amount is required',
                      min: {
                        value: 1,
                        message: 'Target amount must be greater than 0',
                      },
                    }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <InputNumber
                        id="targetAmount"
                        className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                        formatter={formatCurrencyInput}
                        parser={parseCurrencyInput}
                        min={0}
                        controls={false}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        ref={ref}
                      />
                    )}
                  />
                  {errors.targetAmount && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.targetAmount.message}
                    </p>
                  )}
                </div>

                {/* Month Interval Field */}
                <div>
                  <label className="text-md block text-gray-700">
                    Savings Interval (Months)
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={MIN_MONTHS}
                    max={MAX_MONTHS}
                    onKeyDown={blockNonIntegerKeys}
                    {...register('monthInterval', {
                      required: 'Month interval is required',
                      min: {
                        value: MIN_MONTHS,
                        message: `Month interval must be at least ${MIN_MONTHS}`,
                      },
                      max: {
                        value: MAX_MONTHS,
                        message: `Month interval must be at most ${MAX_MONTHS}`,
                      },
                      valueAsNumber: true,
                    })}
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                  {errors.monthInterval && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.monthInterval.message}
                    </p>
                  )}
                </div>
                {explanation && (
                  <p className="text-base text-gray-600">{explanation}</p>
                )}
              </form>
              <div className="flex justify-between space-x-3 p-4">
                <NeutralButton
                  addClass="rounded-full w-[238px] h-[48px]"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  onClick={handleSubmit(onSubmit)}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px]"
                  submit={true}
                  disabled={loading}
                >
                  {loading ? (
                    <ClipLoader size={20} color="#000" />
                  ) : editBudgetItem ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default AddSavingsCategoryModal;
